// Salary — admin payroll page, employee drawer, update-salary modal, staff "My Salary".
import { useState, useEffect, useMemo } from "react";
import { useMobile, showToast } from "../ui/core";
import { T, text, surface, money, fmtDay, fmtDateTime, period, methodLabel, METHODS } from "./ui/tokens";
import {
    StatusBadge, PageHeader, FinanceStatCard, StatGrid, Tabs, Notice, DataTable, ActionMenu, ExportMenu,
    Modal, Drawer, DetailList, FormSection, Field, Input, Select, TextArea, Button, PaymentModal, AuditHistory, EmptyState,
} from "./ui/components";
import { computeMonthPayroll, computePendingPayroll, salaryRowOn, upcomingSalaryRow, paidRowFigures, daysInMonth, payrollPolicy } from "./payroll";
import * as api from "./api";
import { downloadSalarySlip } from "./slips";
import { proofLinkProps, openProof } from "./proofs";

// ─── shared helpers ──────────────────────────────────────────────────────────
// Pure calendar maths in UTC so the result never shifts with the device timezone (Nepal is UTC+5:45).
const addDays = (dateStr, n) => { const d = new Date(`${dateStr}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

export function lastFinalizedDay(payments, staffId) {
    let best = null;
    for (const p of payments) {
        if (p.staff_id !== staffId || p.status !== "paid") continue;
        const end = `${p.year}-${String(p.month).padStart(2, "0")}-${String(daysInMonth(p.year, p.month)).padStart(2, "0")}`;
        if (!best || end > best) best = end;
    }
    return best;
}

function payrollCtx(ctx) {
    return {
        staff: ctx.staff, salaryPayments: ctx.salaryPayments, today: ctx.today,
        salaryRowsFor: ctx.salaryRowsFor, hasAttendanceInMonth: ctx.hasAttendanceInMonth, statusForDate: ctx.statusForDate,
    };
}

function figuresForPaid(ctx, row) {
    return paidRowFigures(row, () => computeMonthPayroll({
        rows: ctx.salaryRowsFor(row.staff_id), year: row.year, month: row.month, today: ctx.today,
        statusForDate: (d) => ctx.statusForDate(row.staff_id, d),
        policy: payrollPolicy(ctx.staff.find(s => s.id === row.staff_id)),
    }));
}

function slipForPaid(ctx, row) {
    const fig = figuresForPaid(ctx, row);
    const s = ctx.staff.find(x => x.id === row.staff_id);
    return downloadSalarySlip({
        year: row.year, month: row.month,
        employee: fig.employee || { name: s?.name || row.staff_name, designation: s?.designation },
        fig, pay: { status: "paid", amount: row.amount, payment_method: row.payment_method, txn_number: row.txn_number, paid_at: row.paid_at },
    });
}

function slipForPending(ctx, r) {
    const s = ctx.staff.find(x => x.id === r.staff_id);
    return downloadSalarySlip({
        year: r.year, month: r.month, employee: { name: s?.name || r.staff_name, designation: s?.designation },
        fig: { grossSalary: r.grossSalary, deduction: r.deduction, netSalary: r.amount, present: r.present, absent: r.absent,
            holidays: r.holidays, periods: r.periods, daysInMonth: daysInMonth(r.year, r.month), needsReview: r.needsReview },
        pay: { status: "pending" },
    });
}

function Breakdown({ fig, year, month }) {
    const dim = fig.daysInMonth || daysInMonth(year, month);
    const periods = fig.periods || [];
    return (
        <div style={{ ...surface.inset, padding: 14 }}>
            {periods.length > 1 && (
                <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                    {periods.map((p, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
                            <span style={{ color: T.textMuted }}>{money(p.monthly_salary)}/mo × {p.days}/{dim} days <span style={{ color: T.textFaint }}>({fmtDay(p.from)} – {fmtDay(p.to)})</span></span>
                            <span style={text.money}>{money(p.amount)}</span>
                        </div>
                    ))}
                </div>
            )}
            <DetailList items={[
                [periods.length > 1 ? "Gross (prorated)" : "Gross salary", money(fig.grossSalary)],
                [`Absent deduction (${fig.absent || 0} day${fig.absent === 1 ? "" : "s"})`, <span style={{ color: fig.deduction ? T.danger : T.textStrong }}>{money(-(fig.deduction || 0))}</span>],
                ["Net salary", <strong style={{ color: T.success, fontSize: 16 }}>{money(fig.netSalary ?? fig.amount)}</strong>],
            ]} />
            {fig.attendanceAffectsPayroll === false && (
                <div style={{ ...text.caption, marginTop: 10, color: T.info }}>Attendance does not affect this employee's salary: absences are shown but not deducted.</div>
            )}
            <div style={{ ...text.caption, marginTop: 10 }}>
                Present {fig.present ?? 0} · Absent {fig.absent ?? 0} · Holidays {fig.holidays ?? 0} · Saturdays {fig.saturdays ?? 0}
            </div>
        </div>
    );
}

// ─── Update Salary modal ─────────────────────────────────────────────────────
export function UpdateSalaryModal({ open, staff, rows, lockedUntil, today, onClose, onSaved }) {
    const [amount, setAmount] = useState("");
    const [from, setFrom] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => { if (open) { setAmount(""); setFrom(""); setReason(""); setErr(""); } }, [open]);
    if (!open || !staff) return null;

    const minDate = lockedUntil ? addDays(lockedUntil, 1) : undefined;
    const current = salaryRowOn(rows, today);
    const before = from ? salaryRowOn(rows, addDays(from, -1)) : null;
    let preview = null;
    if (from && Number(amount) >= 0 && amount !== "") {
        const [y, m, d] = from.split("-").map(Number);
        const dim = daysInMonth(y, m);
        if (d > 1 && before) {
            preview = `${period(y, m)} will be prorated: ${d - 1} day${d - 1 === 1 ? "" : "s"} at ${money(before.monthly_salary)} and ${dim - d + 1} days at ${money(amount)}.`;
        } else {
            preview = `${money(amount)}/month applies from ${fmtDay(from)}${d === 1 ? ` (all of ${period(y, m)})` : ""}.`;
        }
    }

    const save = async () => {
        setErr("");
        if (amount === "" || Number(amount) < 0) { setErr("Enter the new monthly salary."); return; }
        if (!from) { setErr("Choose the effective date."); return; }
        if (minDate && from < minDate) { setErr(`Payroll is finalized through ${fmtDay(lockedUntil)}. Choose ${fmtDay(minDate)} or later.`); return; }
        setBusy(true);
        try {
            await api.setStaffSalary(staff.id, Number(amount), from, reason.trim());
            showToast(`Salary updated for ${staff.name}`);
            await onSaved?.();
            onClose();
        } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };

    return (
        <Modal open={open} title="Update salary" subtitle={staff.name} onClose={busy ? undefined : onClose}
            footer={<>
                <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Update salary"}</Button>
            </>}>
            <div style={{ marginBottom: 16 }}>
                <Notice tone="info">This will not modify previously finalized salary periods.{lockedUntil ? ` Payroll is finalized through ${fmtDay(lockedUntil)}.` : ""}</Notice>
            </div>
            <FormSection>
                {current && <div style={text.caption}>Current: <strong style={{ color: T.textStrong }}>{money(current.monthly_salary)}/month</strong> since {fmtDay(current.effective_from)}</div>}
                <Field label="New monthly salary (NPR)" required>
                    <Input type="number" inputMode="decimal" min="0" value={amount} onChange={e => { setAmount(e.target.value); setErr(""); }} placeholder="e.g. 30000" autoFocus />
                </Field>
                <Field label="Effective from" required hint="The first day the new salary applies. Mid-month dates are prorated by day.">
                    <Input type="date" value={from} min={minDate} onChange={e => { setFrom(e.target.value); setErr(""); }} />
                </Field>
                <Field label="Reason / note"><TextArea value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Annual increment" /></Field>
            </FormSection>
            {preview && <div style={{ ...surface.inset, padding: 12, fontSize: 13, color: T.text, marginBottom: 12 }}>{preview}</div>}
            {err && <Notice tone="danger">{err}</Notice>}
        </Modal>
    );
}

// ─── Employee salary drawer ──────────────────────────────────────────────────
// Small labels for the employee's payroll policy (shown wherever the employee is listed).
function PolicyBadges({ policy }) {
    if (!policy) return null;
    const items = [];
    if (policy.isTest) items.push(<StatusBadge key="t" status="draft" label="Test account" title="Excluded from all real company financial figures" />);
    if (policy.terminated) items.push(<StatusBadge key="f" status="legacy" label={policy.endDate ? `Former · left ${fmtDay(policy.endDate)}` : "Former · end date needed"} />);
    if (!policy.attendanceAffectsPayroll) items.push(<StatusBadge key="a" status="approved" label="No attendance deductions" />);
    if (!items.length) return null;
    return <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>{items}</div>;
}

const AUDIT_FIELD = {
    employment_status: "Employment status", employment_end_date: "Final working day",
    attendance_affects_payroll: "Attendance affects payroll", is_test_account: "Test account",
};

// Admin-only payroll settings (audited in staff_payroll_audit with the reason).
function PayrollSettingsModal({ open, staff, onClose, onSaved }) {
    const [status, setStatus] = useState("active");
    const [endDate, setEndDate] = useState("");
    const [att, setAtt] = useState(true);
    const [isTest, setIsTest] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        if (!open || !staff) return;
        const p = payrollPolicy(staff);
        setStatus(p.terminated ? "terminated" : "active"); setEndDate(p.endDate || ""); setAtt(p.attendanceAffectsPayroll);
        setIsTest(p.isTest); setReason(""); setErr("");
    }, [open, staff]);
    if (!open || !staff) return null;
    const save = async () => {
        setErr("");
        if (!reason.trim()) { setErr("Enter the reason for this change (kept in the audit history)."); return; }
        setBusy(true);
        try {
            await api.setPayrollSettings(staff.id, { status, endDate: status === "terminated" ? endDate : null, attendanceAffectsPayroll: att, isTest, reason: reason.trim() });
            showToast(`Payroll settings updated for ${staff.name}`);
            await onSaved?.(); onClose();
        } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    const check = (checked, set, label, hint) => (
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={checked} onChange={e => { set(e.target.checked); setErr(""); }} style={{ width: 18, height: 18, marginTop: 2, accentColor: T.primary }} />
            <span><span style={{ color: T.textStrong, fontSize: 14 }}>{label}</span><span style={{ ...text.caption, display: "block", marginTop: 2 }}>{hint}</span></span>
        </label>
    );
    return (
        <Modal open={open} title="Payroll settings" subtitle={staff.name} onClose={busy ? undefined : onClose}
            footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save settings"}</Button></>}>
            <FormSection title="Employment">
                <Field label="Employment status">
                    <Select value={status} onChange={e => { setStatus(e.target.value); setErr(""); }}>
                        <option value="active">Active</option>
                        <option value="terminated">Terminated (former employee)</option>
                    </Select>
                </Field>
                {status === "terminated" && (
                    <Field label="Final working day" hint="Leave empty until confirmed. Without it, no further payroll is generated for this person.">
                        <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setErr(""); }} />
                    </Field>
                )}
            </FormSection>
            <FormSection title="Payroll rules">
                {check(att, setAtt, "Attendance affects payroll", "When off, salary is not reduced for days without clock-in. Attendance records are kept unchanged.")}
                {check(isTest, setIsTest, "Test account", "Excluded from real payroll totals, outstanding payroll and all company financial figures.")}
            </FormSection>
            <Field label="Reason for change" required><TextArea value={reason} onChange={e => { setReason(e.target.value); setErr(""); }} placeholder="Recorded in the audit history" /></Field>
            {err && <div style={{ marginTop: 12 }}><Notice tone="danger">{err}</Notice></div>}
        </Modal>
    );
}

function EmployeeSalaryDrawer({ ctx, staffId, onClose, onUpdate }) {
    const s = ctx.staff.find(x => x.id === staffId);
    const rows = staffId ? ctx.salaryRowsFor(staffId) : [];
    const [busy, setBusy] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [audit, setAudit] = useState([]);
    useEffect(() => {
        let live = true;
        if (staffId) api.getPayrollAudit(staffId).then(a => { if (live) setAudit(a || []); }).catch(() => {});
        return () => { live = false; };
    }, [staffId, s?.employment_status, s?.employment_end_date, s?.attendance_affects_payroll, s?.is_test_account]);
    if (!s) return null;
    const current = salaryRowOn(rows, ctx.today);
    const upcoming = upcomingSalaryRow(rows, ctx.today);
    const locked = lastFinalizedDay(ctx.salaryPayments, staffId);
    const review = rows.filter(r => r.review_status === "unverified");
    const payments = ctx.salaryPayments.filter(p => p.staff_id === staffId && p.status === "paid")
        .sort((a, b) => (b.year - a.year) || (b.month - a.month)).slice(0, 6);

    const act = async (key, fn, msg) => {
        setBusy(key);
        try { await fn(); showToast(msg); await ctx.reload(); } catch (e) { showToast(e.message, T.danger); } finally { setBusy(null); }
    };

    return (
        <Drawer open title={s.name} subtitle={s.designation} onClose={onClose}
            actions={<Button size="sm" onClick={() => onUpdate(s)}>Update salary</Button>}>
            <div style={{ ...surface.inset, padding: 16, marginBottom: 16 }}>
                <div style={text.caption}>Current salary</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: T.textStrong, marginTop: 4 }}>
                    {current ? <>{money(current.monthly_salary)}<span style={{ fontSize: 14, color: T.textMuted, fontWeight: 500 }}>/month</span></> : "Not set"}
                </div>
                {current && <div style={{ ...text.caption, marginTop: 4 }}>Effective from {fmtDay(current.effective_from)}</div>}
                {upcoming && <div style={{ marginTop: 10 }}><StatusBadge status="upcoming" label={`${money(upcoming.monthly_salary)} from ${fmtDay(upcoming.effective_from)}`} /></div>}
            </div>

            {review.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Notice tone="warning">
                        Unverified salary history: created from today's salary during the upgrade, not confirmed as the real
                        historical salary. It is not used as authoritative payroll; confirm it or record the real earlier
                        salaries before releasing payroll.
                    </Notice>
                </div>
            )}
            {locked && <div style={{ ...text.caption, marginBottom: 16 }}>Payroll finalized through {fmtDay(locked)} — earlier periods cannot change.</div>}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 style={text.section}>Payroll settings</h3>
                <Button size="sm" variant="secondary" onClick={() => setSettingsOpen(true)}>Edit</Button>
            </div>
            <div style={{ ...surface.inset, padding: 14, marginBottom: 12 }}>
                <DetailList items={[
                    ["Employment", payrollPolicy(s).terminated ? (s.employment_end_date ? `Terminated · final day ${fmtDay(s.employment_end_date)}` : "Terminated · final day not confirmed") : "Active"],
                    ["Attendance affects payroll", payrollPolicy(s).attendanceAffectsPayroll ? "Yes" : "No"],
                    ["Test account", payrollPolicy(s).isTest ? "Yes (excluded from company figures)" : "No"],
                ]} />
            </div>
            {audit.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <AuditHistory events={audit.map(a => ({
                        label: `${AUDIT_FIELD[a.field] || a.field}: ${a.old_value ?? "—"} → ${a.new_value ?? "—"}${a.reason ? ` (${a.reason})` : ""}`,
                        by: a.changed_by ? ctx.staffName(a.changed_by) : "system migration", at: a.changed_at, tone: T.info,
                    }))} />
                </div>
            )}
            <PayrollSettingsModal open={settingsOpen} staff={s} onClose={() => setSettingsOpen(false)} onSaved={ctx.reload} />

            <h3 style={{ ...text.section, marginBottom: 10 }}>Salary history</h3>
            {rows.length === 0 ? <EmptyState title="No salary recorded" hint="Use Update salary to set one." /> : (
                <div style={{ display: "grid", gap: 8, marginBottom: 24 }}>
                    {[...rows].reverse().map(r => {
                        const isCurrent = current && r.id === current.id;
                        const canRemove = r.id && (!locked || r.effective_from > locked);
                        return (
                            <div key={r.id || r.effective_from} style={{ ...surface.inset, padding: 12, display: "flex", gap: 12, alignItems: "flex-start" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                        <span style={{ ...text.money, fontSize: 15 }}>{money(r.monthly_salary)}</span>
                                        {isCurrent && <StatusBadge status="current" />}
                                        {r.effective_from > ctx.today && <StatusBadge status="upcoming" />}
                                        {r.review_status === "unverified" ? <StatusBadge status="unverified" title="Not confirmed history" /> : <StatusBadge status="confirmed" />}
                                    </div>
                                    <div style={{ fontSize: 13, color: T.text, marginTop: 3 }}>{fmtDay(r.effective_from)} – {r.effective_to ? fmtDay(r.effective_to) : "Current"}</div>
                                    {r.reason && <div style={{ ...text.caption, marginTop: 3 }}>{r.reason}</div>}
                                    {r.created_at && <div style={{ ...text.caption, marginTop: 3 }}>Recorded {fmtDateTime(r.created_at)}{r.created_by ? ` by ${ctx.staffName(r.created_by)}` : ""}</div>}
                                </div>
                                {r.id && (
                                    <ActionMenu items={[
                                        r.review_status === "unverified" && { label: busy === r.id ? "Confirming…" : "Confirm as correct", onClick: () => act(r.id, () => api.confirmSalaryHistory(r.id), "Salary history confirmed") },
                                        canRemove && { label: "Remove", danger: true, onClick: () => act(r.id, () => api.deleteSalaryHistory(r.id), "Salary entry removed") },
                                    ]} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <h3 style={{ ...text.section, marginBottom: 10 }}>Recent payments</h3>
            {payments.length === 0 ? <div style={text.caption}>No salary paid yet.</div> : (
                <div style={{ display: "grid", gap: 8 }}>
                    {payments.map(p => (
                        <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", fontSize: 14 }}>
                            <span>{period(p.year, p.month)}</span>
                            <span style={{ display: "flex", gap: 8, alignItems: "center" }}><span style={text.money}>{money(p.amount)}</span>
                                <StatusBadge status={p.calc_snapshot ? "finalized" : "legacy"} /></span>
                        </div>
                    ))}
                </div>
            )}
        </Drawer>
    );
}

// ─── Paid salary detail drawer ───────────────────────────────────────────────
function PaidSalaryDrawer({ ctx, row, onClose }) {
    if (!row) return null;
    const fig = figuresForPaid(ctx, row);
    return (
        <Drawer open title={`${row.staff_name} · ${period(row.year, row.month)}`} subtitle="Salary payment" onClose={onClose}
            actions={<Button size="sm" variant="secondary" onClick={() => slipForPaid(ctx, row)}>Payslip</Button>}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <StatusBadge status={fig.source === "finalized" ? "finalized" : "legacy"} />
            </div>
            {fig.source === "legacy" && (
                <div style={{ marginBottom: 16 }}><Notice tone="info">Paid before payroll finalization existed. Only the amount, date and reference were stored; the breakdown below is recalculated from current records.</Notice></div>
            )}
            <h3 style={{ ...text.section, marginBottom: 10 }}>Calculation</h3>
            <div style={{ marginBottom: 20 }}><Breakdown fig={fig} year={row.year} month={row.month} /></div>
            <h3 style={{ ...text.section, marginBottom: 10 }}>Payment</h3>
            <div style={{ ...surface.inset, padding: 14, marginBottom: 20 }}>
                <DetailList items={[
                    ["Amount paid", <strong>{money(row.amount)}</strong>],
                    ["Paid on", fmtDateTime(row.paid_at)],
                    ["Method", methodLabel(row.payment_method)],
                    ["Reference", row.txn_number || "—"],
                    ["Proof", row.payment_screenshot ? <a {...proofLinkProps(row.payment_screenshot)} style={{ color: T.primary }}>View proof</a> : "—"],
                    row.note && ["Notes", row.note],
                ]} />
            </div>
            <h3 style={{ ...text.section, marginBottom: 10 }}>History</h3>
            <AuditHistory events={[
                row.finalized_at
                    ? { label: "Payroll finalized", by: row.paid_by ? ctx.staffName(row.paid_by) : null, at: row.finalized_at, tone: T.success }
                    : { label: "Recorded as paid", at: row.paid_at || row.created_at, tone: T.textFaint },
            ]} />
        </Drawer>
    );
}

// ─── Admin: Salaries page ────────────────────────────────────────────────────
export function SalaryAdminPage({ ctx }) {
    const mobile = useMobile();
    const [tab, setTab] = useState("employees");
    const [drawerId, setDrawerId] = useState(null);
    const [updateFor, setUpdateFor] = useState(null);
    const [payFor, setPayFor] = useState(null);
    const [serverPreview, setServerPreview] = useState(null);
    const [paidDetail, setPaidDetail] = useState(null);
    const [fStaff, setFStaff] = useState("all");
    const [fYear, setFYear] = useState("all");
    const [fGroup, setFGroup] = useState("active");

    const pending = useMemo(() => computePendingPayroll(payrollCtx(ctx)), [ctx]);
    const paid = useMemo(() => ctx.salaryPayments.filter(p => p.status === "paid"), [ctx.salaryPayments]);
    const salaried = ctx.staff.filter(s => s.employment_type !== "freelancer");

    const employees = salaried.map(s => {
        const rows = ctx.salaryRowsFor(s.id);
        const current = salaryRowOn(rows, ctx.today);
        const upcoming = upcomingSalaryRow(rows, ctx.today);
        const unpaid = pending.filter(p => p.staff_id === s.id).length;
        const last = paid.filter(p => p.staff_id === s.id).sort((a, b) => (b.year - a.year) || (b.month - a.month))[0];
        const needsReview = rows.some(r => r.review_status === "unverified");
        const policy = payrollPolicy(s);
        return { ...s, current, upcoming, unpaid, last, needsReview, policy, hasSalary: rows.length > 0 };
    }).filter(e => e.hasSalary || Number(e.salary) > 0);
    const isTestStaff = (staffId) => payrollPolicy(ctx.staff.find(s => s.id === staffId)).isTest;
    const groupOf = (e) => (e.policy.isTest ? "test" : e.policy.terminated ? "former" : "active");
    const employeesShown = employees.filter(e => fGroup === "all" || groupOf(e) === fGroup);
    const realActive = employees.filter(e => groupOf(e) === "active");

    const years = [...new Set([...pending, ...paid].map(r => r.year))].sort((a, b) => b - a);
    const applyF = (list) => list.filter(r => (fStaff === "all" || r.staff_id === fStaff) && (fYear === "all" || r.year === Number(fYear)));
    const pendingF = applyF(pending);
    const paidF = applyF(paid);

    const thisYear = new Date().getFullYear();
    // Company figures: real employees only (test accounts never count).
    const monthlyPayroll = realActive.reduce((a, e) => a + Number(e.current?.monthly_salary || 0), 0);
    const outstanding = pending.filter(r => !r.isTest).reduce((a, r) => a + Number(r.amount), 0);
    const paidReal = paid.filter(p => !isTestStaff(p.staff_id));
    const paidThisYear = paidReal.filter(p => p.year === thisYear).reduce((a, r) => a + Number(r.amount), 0);
    const reviewCount = employees.filter(e => e.needsReview && !e.policy.isTest).length;

    useEffect(() => {
        if (!payFor) { setServerPreview(null); return; }
        let live = true;
        api.previewSalaryMonth(payFor.staff_id, payFor.year, payFor.month)
            .then(p => { if (live) setServerPreview(p); })
            .catch(() => { if (live) setServerPreview(null); });
        return () => { live = false; };
    }, [payFor]);

    const payFig = payFor ? (serverPreview ? {
        grossSalary: Number(serverPreview.gross_salary), deduction: Number(serverPreview.deduction), netSalary: Number(serverPreview.net_salary),
        present: serverPreview.present_days, absent: serverPreview.absent_days, holidays: serverPreview.holiday_days, saturdays: serverPreview.saturday_days,
        periods: serverPreview.periods, daysInMonth: serverPreview.days_in_month, needsReview: serverPreview.needs_review,
        attendanceAffectsPayroll: serverPreview.policy?.attendance_affects_payroll,
    } : { ...payFor, netSalary: payFor.amount, daysInMonth: daysInMonth(payFor.year, payFor.month) }) : null;

    const confirmPay = async ({ amount, paidAt, method, txn, file, notes }) => {
        const proof = await api.uploadProof(file, `salary_${payFor.staff_id}_${payFor.year}_${payFor.month}`);
        try {
            const row = await api.finalizeSalaryPayment({ staffId: payFor.staff_id, year: payFor.year, month: payFor.month, amount, paidAt, method, txn, proof, notes });
            showToast(`Salary for ${period(payFor.year, payFor.month)} paid and finalized`);
            setPayFor(null);
            await ctx.reload();
            setPaidDetail(Array.isArray(row) ? row[0] : row);
            setTab("paid");
        } catch (e) { await api.removeProof(proof); throw e; }
    };

    const staffFilter = (
        <>
            <Select value={fStaff} onChange={e => setFStaff(e.target.value)} style={{ width: mobile ? "100%" : 180 }}>
                <option value="all">All employees</option>
                {salaried.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Select value={fYear} onChange={e => setFYear(e.target.value)} style={{ width: mobile ? "100%" : 120 }}>
                <option value="all">All years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
        </>
    );

    const empCols = [
        { key: "name", header: "Employee", sortValue: r => r.name.toLowerCase(), exportValue: r => r.name,
            render: r => <div><div style={{ fontWeight: 600, color: T.textStrong }}>{r.name}</div><div style={text.caption}>{r.designation}</div><PolicyBadges policy={r.policy} /></div> },
        { key: "salary", header: "Current salary", align: "right", sortValue: r => Number(r.current?.monthly_salary || 0), exportValue: r => r.current?.monthly_salary ?? "",
            render: r => r.current ? <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>{r.current.review_status === "unverified" && <StatusBadge status="unverified" />}<span style={text.money}>{money(r.current.monthly_salary)}</span></span> : <span style={text.caption}>Not set</span> },
        { key: "since", header: "Effective since", sortValue: r => r.current?.effective_from || "", exportValue: r => r.current?.effective_from || "",
            render: r => <div>{fmtDay(r.current?.effective_from)}{r.upcoming && <div style={text.caption}>→ {money(r.upcoming.monthly_salary)} from {fmtDay(r.upcoming.effective_from)}</div>}</div> },
        { key: "status", header: "Status", exportValue: r => (r.needsReview ? "Needs review" : r.unpaid ? `${r.unpaid} unpaid` : "Up to date"),
            render: r => r.needsReview ? <StatusBadge status="review" /> : r.unpaid ? <StatusBadge status="pending" label={`${r.unpaid} unpaid month${r.unpaid > 1 ? "s" : ""}`} /> : <StatusBadge status="paid" label="Up to date" /> },
        { key: "last", header: "Last paid", sortValue: r => (r.last ? r.last.year * 100 + r.last.month : 0), exportValue: r => (r.last ? period(r.last.year, r.last.month) : ""),
            render: r => (r.last ? period(r.last.year, r.last.month) : <span style={text.caption}>—</span>) },
    ];

    const pendCols = [
        { key: "emp", header: "Employee", sortValue: r => r.staff_name.toLowerCase(), exportValue: r => r.staff_name, render: r => <strong style={{ color: T.textStrong, fontWeight: 600 }}>{r.staff_name}</strong> },
        { key: "period", header: "Period", nowrap: true, sortValue: r => r.year * 100 + r.month, exportValue: r => period(r.year, r.month), render: r => period(r.year, r.month) },
        { key: "gross", header: "Gross", align: "right", hideOnMobile: true, exportValue: r => r.grossSalary, render: r => money(r.grossSalary) },
        { key: "ded", header: "Deduction", align: "right", hideOnMobile: true, exportValue: r => r.deduction, render: r => <span style={{ color: r.deduction ? T.danger : T.textMuted }}>{money(-r.deduction)}</span> },
        { key: "net", header: "Net", align: "right", sortValue: r => Number(r.amount), exportValue: r => r.amount, render: r => <span style={text.money}>{money(r.amount)}</span> },
        { key: "att", header: "Attendance", hideOnMobile: true, exportValue: r => `P ${r.present} / A ${r.absent}${r.attendanceAffectsPayroll === false ? " (not deducted)" : ""}`,
            render: r => <span style={text.caption}>P {r.present} · A {r.absent}{r.attendanceAffectsPayroll === false && <><br />not deducted</>}</span> },
        { key: "status", header: "Status", exportValue: r => (r.needsReview ? "Needs review" : "Outstanding"), render: r => <StatusBadge status={r.needsReview ? "review" : "outstanding"} /> },
    ];

    const paidCols = [
        { key: "emp", header: "Employee", sortValue: r => (r.staff_name || "").toLowerCase(), exportValue: r => r.staff_name, render: r => <strong style={{ color: T.textStrong, fontWeight: 600 }}>{r.staff_name}</strong> },
        { key: "period", header: "Period", nowrap: true, sortValue: r => r.year * 100 + r.month, exportValue: r => period(r.year, r.month), render: r => period(r.year, r.month) },
        { key: "amount", header: "Amount paid", align: "right", sortValue: r => Number(r.amount), exportValue: r => r.amount, render: r => <span style={text.money}>{money(r.amount)}</span> },
        { key: "paid", header: "Paid on", nowrap: true, sortValue: r => r.paid_at || "", exportValue: r => fmtDay(r.paid_at), render: r => fmtDay(r.paid_at) },
        { key: "method", header: "Method · Ref", hideOnMobile: true, exportValue: r => `${methodLabel(r.payment_method)} ${r.txn_number || ""}`.trim(),
            render: r => <div><div>{methodLabel(r.payment_method)}</div>{r.txn_number && <div style={text.caption}>{r.txn_number}</div>}</div> },
        { key: "status", header: "Status", exportValue: r => (r.calc_snapshot ? "Finalized" : "Legacy"), render: r => <StatusBadge status={r.calc_snapshot ? "finalized" : "legacy"} title={r.calc_snapshot ? undefined : "Paid before payroll finalization existed"} /> },
    ];

    return (
        <div>
            <PageHeader title="Salaries" subtitle="Payroll from attendance · AD calendar months · salary changes prorated by day" />

            {reviewCount > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Notice tone="warning" action={<Button size="sm" variant="secondary" onClick={() => setTab("employees")}>Review</Button>}>
                        {reviewCount} employee{reviewCount > 1 ? "s have" : " has"} unverified salary history. Payroll for them can't be finalized until it is confirmed.
                    </Notice>
                </div>
            )}

            <StatGrid>
                <FinanceStatCard label="Monthly payroll" value={money(monthlyPayroll)} sub={`${realActive.length} active employees`} tone={T.primary} />
                <FinanceStatCard label="Outstanding" value={money(outstanding)} sub={`${pending.length} month${pending.length === 1 ? "" : "s"} unpaid`} tone={T.warning} />
                <FinanceStatCard label={`Paid in ${thisYear}`} value={money(paidThisYear)} sub={`${paidReal.filter(p => p.year === thisYear).length} payments`} tone={T.success} />
                <FinanceStatCard label="Needs review" value={reviewCount} sub="salary histories" tone={reviewCount ? T.warning : T.textFaint} />
            </StatGrid>

            <Tabs value={tab} onChange={setTab} items={[["employees", "Employees", realActive.length], ["outstanding", "Outstanding", pendingF.length], ["paid", "Paid", paidF.length]]} />

            {tab === "employees" && (
                <DataTable columns={empCols} rows={employeesShown} searchText={r => `${r.name} ${r.designation || ""}`} searchPlaceholder="Search employees…"
                    filters={<Select value={fGroup} onChange={e => setFGroup(e.target.value)} style={{ width: mobile ? "100%" : 200 }} aria-label="Employee group">
                        <option value="active">Active employees</option>
                        <option value="former">Former employees</option>
                        <option value="test">Test accounts</option>
                        <option value="all">All</option>
                    </Select>}
                    defaultSort={{ key: "name", dir: "asc" }} onRowClick={r => setDrawerId(r.id)}
                    toolbarRight={<ExportMenu filename="salaries_employees" title="Employee salaries" columns={empCols} rows={employees} />}
                    rowActions={r => <>
                        <Button size="sm" variant="secondary" onClick={() => setUpdateFor(r)}>Update salary</Button>
                        <ActionMenu items={[{ label: "View details", onClick: () => setDrawerId(r.id) }, { label: "Salary history", onClick: () => setDrawerId(r.id) }]} />
                    </>}
                    mobileCard={r => (
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: T.textStrong }}>{r.name}</div>
                                <div style={text.caption}>{r.designation}</div>
                                <PolicyBadges policy={r.policy} />
                                <div style={{ marginTop: 6 }}>{r.needsReview ? <StatusBadge status="review" /> : r.unpaid ? <StatusBadge status="pending" label={`${r.unpaid} unpaid`} /> : <StatusBadge status="paid" label="Up to date" />}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={text.money}>{r.current ? money(r.current.monthly_salary) : "—"}</div>
                                <div style={text.caption}>since {fmtDay(r.current?.effective_from)}</div>
                                <div style={text.caption}>Last paid {r.last ? period(r.last.year, r.last.month) : "—"}</div>
                            </div>
                        </div>
                    )}
                    empty="No salaried employees yet." />
            )}

            {tab === "outstanding" && (
                <DataTable columns={pendCols} rows={pendingF} rowKey={r => r.id} searchText={r => r.staff_name} filters={staffFilter}
                    defaultSort={{ key: "period", dir: "asc" }}
                    toolbarRight={<ExportMenu filename="salaries_outstanding" title="Outstanding salaries" columns={pendCols} rows={pendingF} />}
                    rowActions={r => <>
                        <Button size="sm" variant="success" onClick={() => setPayFor(r)}>Mark as paid</Button>
                        <ActionMenu items={[{ label: "Preview payslip", onClick: () => slipForPending(ctx, r) }, { label: "View employee", onClick: () => setDrawerId(r.staff_id) }]} />
                    </>}
                    mobileCard={r => (
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <div><div style={{ fontWeight: 600, color: T.textStrong }}>{r.staff_name}</div><div style={text.caption}>{period(r.year, r.month)} · P {r.present} · A {r.absent}</div>
                                <div style={{ marginTop: 6 }}><StatusBadge status={r.needsReview ? "review" : "outstanding"} /></div></div>
                            <div style={{ textAlign: "right" }}><div style={{ ...text.money, fontSize: 16 }}>{money(r.amount)}</div>{r.deduction > 0 && <div style={{ ...text.caption, color: T.danger }}>{money(-r.deduction)}</div>}</div>
                        </div>
                    )}
                    empty="All salaries are up to date." />
            )}

            {tab === "paid" && (
                <DataTable columns={paidCols} rows={paidF} searchText={r => `${r.staff_name} ${r.txn_number || ""}`} filters={staffFilter}
                    defaultSort={{ key: "period", dir: "desc" }} onRowClick={r => setPaidDetail(r)}
                    toolbarRight={<ExportMenu filename="salaries_paid" title="Paid salaries" columns={paidCols} rows={paidF} />}
                    rowActions={r => <>
                        <Button size="sm" variant="secondary" onClick={() => slipForPaid(ctx, r)}>Payslip</Button>
                        <ActionMenu items={[{ label: "View details", onClick: () => setPaidDetail(r) }, r.payment_screenshot && { label: "View proof", onClick: () => openProof(r.payment_screenshot) }]} />
                    </>}
                    mobileCard={r => (
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <div><div style={{ fontWeight: 600, color: T.textStrong }}>{r.staff_name}</div><div style={text.caption}>{period(r.year, r.month)} · {fmtDay(r.paid_at)}</div>
                                <div style={{ marginTop: 6 }}><StatusBadge status={r.calc_snapshot ? "finalized" : "legacy"} /></div></div>
                            <div style={{ textAlign: "right" }}><div style={{ ...text.money, fontSize: 16 }}>{money(r.amount)}</div><div style={text.caption}>{methodLabel(r.payment_method)}</div></div>
                        </div>
                    )}
                    empty="No salary has been paid yet." />
            )}

            {drawerId && <EmployeeSalaryDrawer ctx={ctx} staffId={drawerId} onClose={() => setDrawerId(null)} onUpdate={(s) => setUpdateFor(s)} />}
            <UpdateSalaryModal open={!!updateFor} staff={updateFor} rows={updateFor ? ctx.salaryRowsFor(updateFor.id) : []}
                lockedUntil={updateFor ? lastFinalizedDay(ctx.salaryPayments, updateFor.id) : null} today={ctx.today}
                onClose={() => setUpdateFor(null)} onSaved={ctx.reload} />
            <PaymentModal open={!!payFor} title="Release salary" subtitle={payFor ? `${payFor.staff_name} · ${period(payFor.year, payFor.month)}` : ""}
                methods={METHODS} defaultAmount={payFig?.netSalary} amountHint={payFig ? `Calculated net salary: ${money(payFig.netSalary)}` : undefined}
                blocked={payFig?.needsReview ? <>Confirm {payFor.staff_name}'s salary history before releasing payroll. <a href="#" onClick={(e) => { e.preventDefault(); const id = payFor.staff_id; setPayFor(null); setDrawerId(id); }} style={{ color: T.primary }}>Review history</a></> : null}
                summary={payFig && <>
                    <Breakdown fig={payFig} year={payFor.year} month={payFor.month} />
                    <div style={{ ...text.caption, marginTop: 8 }}>Once confirmed, this payroll is finalized: later salary, attendance or profile changes will not alter it.</div>
                </>}
                confirmLabel="Confirm payment" onConfirm={confirmPay} onClose={() => setPayFor(null)} />
            <PaidSalaryDrawer ctx={ctx} row={paidDetail} onClose={() => setPaidDetail(null)} />
        </div>
    );
}

// ─── Staff: My Salary ────────────────────────────────────────────────────────
export function MySalaryPage({ ctx }) {
    const [tab, setTab] = useState("pending");
    const [detail, setDetail] = useState(null);
    const me = ctx.staff.find(s => s.id === ctx.user.id);
    const pending = useMemo(() => computePendingPayroll(payrollCtx(ctx), ctx.user.id), [ctx]);
    const paid = ctx.salaryPayments.filter(r => r.staff_id === ctx.user.id && r.status === "paid")
        .sort((a, b) => (b.year - a.year) || (b.month - a.month));
    const current = salaryRowOn(ctx.salaryRowsFor(ctx.user.id), ctx.today);
    const totalOut = pending.reduce((a, r) => a + Number(r.amount), 0);
    const totalPaid = paid.reduce((a, r) => a + Number(r.amount), 0);

    const pendCols = [
        { key: "period", header: "Period", nowrap: true, sortValue: r => r.year * 100 + r.month, render: r => period(r.year, r.month) },
        { key: "gross", header: "Gross", align: "right", render: r => money(r.grossSalary) },
        { key: "ded", header: "Deduction", align: "right", render: r => <span style={{ color: r.deduction ? T.danger : T.textMuted }}>{money(-r.deduction)}</span> },
        { key: "net", header: "Net", align: "right", render: r => <span style={text.money}>{money(r.amount)}</span> },
        { key: "att", header: "Attendance", hideOnMobile: true, render: r => <span style={text.caption}>P {r.present} · A {r.absent}</span> },
        { key: "status", header: "Status", render: r => <StatusBadge status={r.needsReview ? "review" : "outstanding"} title={r.needsReview ? "Estimate: salary history not yet confirmed" : undefined} /> },
    ];
    const paidCols = [
        { key: "period", header: "Period", nowrap: true, sortValue: r => r.year * 100 + r.month, render: r => period(r.year, r.month) },
        { key: "amount", header: "Amount paid", align: "right", render: r => <span style={text.money}>{money(r.amount)}</span> },
        { key: "paid", header: "Paid on", nowrap: true, render: r => fmtDay(r.paid_at) },
        { key: "method", header: "Method", hideOnMobile: true, render: r => <div>{methodLabel(r.payment_method)}{r.txn_number && <div style={text.caption}>{r.txn_number}</div>}</div> },
        { key: "status", header: "Status", render: r => <StatusBadge status={r.calc_snapshot ? "finalized" : "legacy"} /> },
    ];

    return (
        <div>
            <PageHeader title="My salary" subtitle="Calculated from your attendance records" />
            <StatGrid>
                <FinanceStatCard label="Current salary" value={current ? money(current.monthly_salary) : "—"} sub={current ? `since ${fmtDay(current.effective_from)}` : undefined} tone={T.primary} />
                <FinanceStatCard label="Outstanding" value={money(totalOut)} sub={`${pending.length} month${pending.length === 1 ? "" : "s"}`} tone={T.warning} />
                <FinanceStatCard label="Paid" value={money(totalPaid)} sub={`${paid.length} payment${paid.length === 1 ? "" : "s"}`} tone={T.success} />
                <FinanceStatCard label="Total" value={money(totalOut + totalPaid)} sub="outstanding + paid" />
            </StatGrid>
            <Tabs value={tab} onChange={setTab} items={[["pending", "Outstanding", pending.length], ["paid", "Paid", paid.length]]} />
            {tab === "pending" ? (
                <DataTable columns={pendCols} rows={pending} defaultSort={{ key: "period", dir: "desc" }}
                    rowActions={r => <Button size="sm" variant="secondary" onClick={() => slipForPending(ctx, r)}>Payslip</Button>}
                    empty="No outstanding salary — you're all caught up." />
            ) : (
                <DataTable columns={paidCols} rows={paid} defaultSort={{ key: "period", dir: "desc" }} onRowClick={setDetail}
                    rowActions={r => <>
                        <Button size="sm" variant="secondary" onClick={() => slipForPaid(ctx, r)}>Payslip</Button>
                        {r.payment_screenshot && <ActionMenu items={[{ label: "View payment proof", onClick: () => openProof(r.payment_screenshot) }]} />}
                    </>}
                    empty="No paid salary records yet." />
            )}
            {detail && <PaidSalaryDrawer ctx={{ ...ctx, staffName: (id) => (id === me?.id ? me.name : ctx.staffName(id)) }} row={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}
